<template>
  <p class="tw:heading-3">{{ translation.customers.header }}</p>
  <div class="tw:flex tw:flex-col tw:gap-2">
    <EdroneRadioButton
      :label="translation.optionAll.label"
      :helper="translation.optionAll.helper"
      orientation="horizontal"
      unique-id="FreemiumUpgradeDrawer_All"
      :value="Customers.All"
      :checked="selectedCustomersOption === Customers.All"
      controlled
      @click="() => handleImportOptionChange(Customers.All)"
    >
      <template #default>
        <EdroneTooltip :helper="translation.optionAll.tooltip" trigger-event="mouseenter click" />
      </template>
      <template #customFullWidth>
        <FreemiumUpgradeDrawerCustomersTransition :is-visible="selectedCustomersOption === Customers.All">
          <div class="tw:w-full tw:px-8 tw:py-3 tw:bg-blue-100 tw:rounded-md">
            <p class="tw:body-2--bold tw:mb-1">{{ translation.optionAll.planDetails.header }}</p>
            <p class="tw:body-2 tw:text-default-700">
              {{ translation.optionAll.planDetails.contacts.replace("{contacts}", customersCount.toString()) }}
            </p>
          </div>
        </FreemiumUpgradeDrawerCustomersTransition>
      </template>
    </EdroneRadioButton>
    <EdroneTooltip
      :helper="translation.optionRecommended.tooltipDisabled"
      trigger-event="mouseenter"
      :disabled="!!recommendedCustomersCount"
      position="top"
      class="tw:!w-full"
    >
      <EdroneRadioButton
        :label="translation.optionRecommended.label"
        :helper="translation.optionRecommended.desc"
        orientation="horizontal"
        unique-id="FreemiumUpgradeDrawer_Recommended"
        :value="Customers.Recommended"
        :checked="selectedCustomersOption === Customers.Recommended"
        :disabled="!recommendedCustomersCount"
        controlled
        @click="() => handleImportOptionChange(Customers.Recommended)"
      >
        <template #badge>
          <EdroneBadge v-if="!recommendedCustomersCount" :label="translation.optionBadges.unavailable" size="small" />
          <EdroneBadge v-else :label="translation.optionBadges.recommended" type="info" solid size="small" />
        </template>
        <template #default>
          <EdroneTooltip :helper="translation.optionRecommended.tooltip" trigger-event="mouseenter click" />
        </template>
        <template #customFullWidth>
          <FreemiumUpgradeDrawerCustomersTransition :is-visible="selectedCustomersOption === Customers.Recommended">
            <div class="tw:w-full tw:px-8 tw:py-3 tw:bg-blue-100 tw:rounded-md">
              <p class="tw:body-2--bold tw:mb-1">{{ translation.optionRecommended.planDetails.header }}</p>
              <p class="tw:body-2 tw:text-default-700">
                {{
                  translation.optionRecommended.planDetails.contacts.replace(
                    "{contacts}",
                    recommendedCustomersCount.toString()
                  )
                }}
              </p>
              <p class="tw:body-2 tw:text-default-700">
                {{ translation.optionRecommended.planDetails.includes }}
              </p>
            </div>
          </FreemiumUpgradeDrawerCustomersTransition>
        </template>
      </EdroneRadioButton>
    </EdroneTooltip>
    <EdroneTooltip
      :helper="translation.optionManual.tooltipDisabled"
      trigger-event="mouseenter"
      :disabled="customersCount > 1"
      position="top"
      class="tw:!w-full"
    >
      <EdroneRadioButton
        :label="translation.optionManual.label"
        :helper="translation.optionManual.desc"
        orientation="horizontal"
        unique-id="FreemiumUpgradeDrawer_Manual"
        :value="Customers.Manual"
        :checked="selectedCustomersOption === Customers.Manual"
        :disabled="customersCount <= 1"
        controlled
        @click="() => handleImportOptionChange(Customers.Manual)"
      >
        <template #badge>
          <EdroneBadge v-if="customersCount <= 1" :label="translation.optionBadges.unavailable" size="small" />
        </template>
        <template #default>
          <EdroneTooltip :helper="translation.optionManual.tooltip" trigger-event="mouseenter click" />
        </template>
        <template #customFullWidth>
          <FreemiumUpgradeDrawerManageContacts :is-active="selectedCustomersOption === Customers.Manual" />
        </template>
      </EdroneRadioButton>
    </EdroneTooltip>
  </div>
</template>

<script setup lang="ts">
import { inject } from "vue";
import { symbolMachineFreemium } from "vue-app/components/Freemium/Freemium.machine.ts";
import { useFreemiumStore, Customers } from "vue-app/components/Freemium/Freemium.store.ts";
import { storeToRefs } from "pinia";
import { amplitudeSendEvent } from "vue-app/utils/amplitude.ts";
import FreemiumUpgradeDrawerManageContacts from "vue-app/components/Freemium/UpgradeDrawer/FreemiumUpgradeDrawerManageContacts.vue";
import FreemiumUpgradeDrawerCustomersTransition from "vue-app/components/Freemium/UpgradeDrawer/Customers/FreemiumUpgradeDrawerCustomersTransition.vue";
import EdroneRadioButton from "edrone-components/EdroneRadioButton.vue";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";
import EdroneTooltip from "edrone-components/EdroneTooltip.vue";

const translation = window._trans._freemium.upgradeDrawer;

const actor = inject(symbolMachineFreemium);
const store = useFreemiumStore();
const { selectedCustomersOption, recommendedCustomersCount, customersCount } = storeToRefs(store);

function handleImportOptionChange(option: Customers) {
  amplitudeSendEvent("Freemium_CustomersOption_Changed", { option });

  if (option === Customers.Manual) {
    actor?.send({ type: "MANAGE_CONTACTS" });
  } else {
    actor?.send({ type: "SELECT_IMPORT_OPTION", option });
  }
}
</script>
